import { Link } from 'react-router-dom'
import { Utensils, Cake, MapPin, ArrowRight } from 'lucide-react'
import useInView from '../../hooks/useInView'

const BRANCHES = [
  {
    name: 'Desres Kitchen',
    tagline: 'Authentic South African Cuisine',
    location: 'Limpopo, South Africa',
    description: 'Our flagship branch, running for over 25 years. Serving authentic African cuisine, daily bakes, sweet treats, and customised cakes. From Mukokoroshi and Steak platters to fresh bread, scones, and buns — all made with love.',
    highlights: ['Mukokoroshi & Steak Platters', 'Mala A Khuhu & Chicken Liver', 'Fresh Bread & Scones', 'Sweet Treats & Customised Cakes', 'Burgers, Russians & More'],
    icon: Utensils,
    menuLink: '/menu?branch=desres',
  },
  {
    name: "Yahweh's Delight",
    tagline: 'Freshly Baked, Daily',
    location: 'Pretoria, South Africa',
    description: 'Our newly opened bakery branch in Pretoria, bringing the same family tradition to a new community. Freshly baked bread, scones, fatcook, rolls, sandwiches, and kotas — quality baked goods delivered daily.',
    highlights: ['Fresh Bread (Brown & White)', 'Scones & Fatcook', 'School Buns', 'Sandwiches, Kotas & Toast', 'Hotdog & Russian Rolls'],
    icon: Cake,
    menuLink: '/menu?branch=yawehs',
  },
]

function ScrollReveal({ children, delay = 0, className = '' }) {
  const [ref, inView] = useInView()
  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: inView ? 1 : 0,
        transform: inView ? 'translateY(0)' : 'translateY(30px)',
        transition: `opacity 0.7s ease-out ${delay}s, transform 0.7s ease-out ${delay}s`,
      }}
    >
      {children}
    </div>
  )
}

export default function OurStory() {
  return (
    <div>
      <section className="bg-charcoal relative overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-px" style={{ background: 'linear-gradient(90deg, transparent, #E8761A, transparent)' }} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-20 sm:py-28">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-6 h-0.5 bg-terracotta" />
            <span className="font-body text-[11px] font-semibold tracking-[0.25em] uppercase text-terracotta">About Us</span>
          </div>
          <h1 className="font-display text-cream tracking-[0.04em] leading-[0.95]" style={{ fontSize: 'clamp(48px, 6vw, 88px)' }}>
            Our Story
          </h1>
          <p className="text-sm text-muted mt-4 max-w-xl">
            A family business rooted in the tradition of South African cooking.
          </p>
        </div>
      </section>

      <div className="h-px" style={{ background: 'linear-gradient(90deg, transparent, #E8761A, transparent)' }} />

      <section className="bg-umber">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-10 py-20 sm:py-28">
          <ScrollReveal>
            <p className="text-ink-mid leading-relaxed text-lg">
              For over 25 years, Desres Kitchen & Confectionery has been a family business rooted
              in the tradition of South African cooking. What started as a home kitchen in Limpopo
              has grown into a trusted name — serving African cuisine, daily bakes, sweet treats,
              and customised cakes. With the recent opening of Yahweh's Delight in Pretoria,
              we're bringing the same family warmth and quality to a new community. Our commitment
              remains simple: fresh ingredients, generous portions, and food made with heart.
            </p>
          </ScrollReveal>
        </div>
      </section>

      <div className="h-px" style={{ background: 'linear-gradient(90deg, transparent, #E8761A, transparent)' }} />

      <section className="bg-charcoal">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-20 sm:py-28">
          <ScrollReveal>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-6 h-0.5 bg-terracotta" />
              <span className="font-body text-[11px] font-semibold tracking-[0.25em] uppercase text-terracotta">Locations</span>
            </div>
            <h2 className="font-display text-cream tracking-[0.04em] leading-[0.95] mb-12" style={{ fontSize: 'clamp(48px, 6vw, 88px)' }}>
              Our Branches
            </h2>
          </ScrollReveal>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {BRANCHES.map((branch, i) => {
              const Icon = branch.icon
              return (
                <ScrollReveal key={branch.name} delay={0.2 + i * 0.15}>
                  <Link
                    to={branch.menuLink}
                    className="group relative block bg-surface border border-border-dark rounded-2xl shadow-[0_2px_12px_rgba(0,0,0,0.07)] hover:border-terracotta/40 transition-colors overflow-hidden"
                  >
                    <div className="p-8">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-12 h-12 bg-surface-high rounded-xl flex items-center justify-center">
                          <Icon size={22} className="text-ink-mid" />
                        </div>
                        <div>
                          <h3 className="font-display text-cream text-xl tracking-wide">{branch.name}</h3>
                          <p className="font-body text-[11px] font-semibold tracking-[0.25em] uppercase text-terracotta">{branch.tagline}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5 text-sm text-muted mb-4">
                        <MapPin size={14} /> {branch.location}
                      </div>
                      <p className="text-sm text-ink-mid mb-6 leading-relaxed">{branch.description}</p>
                      <div className="space-y-2 mb-6">
                        {branch.highlights.map((item, j) => (
                          <div key={j} className="flex items-center gap-2 text-sm text-cream">
                            <span className="w-1.5 h-1.5 bg-terracotta rounded-full" />
                            {item}
                          </div>
                        ))}
                      </div>
                      <span className="inline-flex items-center gap-1.5 font-body text-[13px] font-bold tracking-[0.1em] uppercase text-terracotta">
                        View Menu <ArrowRight size={14} />
                      </span>
                    </div>
                  </Link>
                </ScrollReveal>
              )
            })}
          </div>
        </div>
      </section>
    </div>
  )
}
